import { Fade, Modal } from '@mui/material';
import { FC, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import colors from 'atoms/colors/colors';
import { font } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Iframe from 'quarks/Iframe';
import Image from 'quarks/Image';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

import Icon from 'molecules/Icon/Icon';

import richTextParser, { RichText } from 'utils/richTextParser';

import type { IComponentSwitchbackRecord } from 'graphql/types';

type TextStyles = keyof typeof font.display;

interface SwitchbackProps extends BasicProps, IComponentSwitchbackRecord {
  backgroundIsDark: boolean;
  background: string;
  switchBody?: IComponentSwitchbackRecord['bodyCopy'];
  switchFeaturedMedia?: IComponentSwitchbackRecord['featuredMedia'];
  inContent: boolean;
}
const borderColor = {
  dark: colors.storm[700],
  light: colors.bubbles[50],
  'blue-light': colors.bubbles[100],
} as const;

type borderType = keyof typeof borderColor;

const ComponentSwitchback: FC<SwitchbackProps> = props => {
  const {
    backgroundIsDark,
    background,
    eyebrow,
    headline,
    headlineSizeDesktop,
    headlineSizeMobile,
    switchBody,
    switchFeaturedMedia,
    features,
    reverse,
    variant,
    externalVideo,
    inContent,
  } = props;

  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Flex
      flexDirection="column"
      flexWrap="nowrap"
      textAlign="left"
      marginX="auto"
      gap="0"
      lg={{
        flexDirection: reverse ? 'row-reverse' : 'row',
        gap: 0,
      }}
      textColor={backgroundIsDark ? 'storm-25' : 'storm-900'}
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex
        flexDirection="column"
        gap="16px"
        lg={{ flex: '0 0 calc(50% - 30px)', width: 'calc(50% - 30px)', maxWidth: '470px' }}
      >
        {eyebrow && (
          <Text
            textTransform="uppercase"
            textStyle="lg"
            fontWeight="bold"
            textColor={backgroundIsDark ? 'lightning-600' : 'wave-800'}
          >
            {eyebrow}
          </Text>
        )}
        {headline && (
          <Heading
            as="h2"
            textStyle={(headlineSizeMobile as TextStyles) || 'md'}
            md={{ textStyle: (headlineSizeDesktop as TextStyles) || 'lg', fontWeight: 'bold' }}
            fontWeight="bold"
          >
            {headline || ''}
          </Heading>
        )}
        {switchBody && (
          <Flex
            fontSize={inContent ? 'textMd' : 'textLg'}
            marginBottom={16}
            md={{ fontSize: inContent ? 'textLg' : 'textXl' }}
            textColor={backgroundIsDark ? 'storm-100' : 'storm-600'}
            css={{
              button: {
                marginTop: '32px',
                textTransform: 'uppercase',
              },
            }}
          >
            <span>{richTextParser(switchBody as any)}</span>
          </Flex>
        )}
        {features && (
          <Flex
            as="ul"
            css={{
              listStyle: 'none',
              padding: 0,
            }}
            flexDirection="column"
            sm={{ flexDirection: variant === 'List' ? 'column' : 'row' }}
            display={variant === 'List' ? 'block' : 'flex'}
            justifyContent={variant === 'List' ? 'unset' : 'space-between'}
          >
            {features.map(feature => (
              <Paragraph
                as="li"
                key={feature.id}
                paddingLeft={variant === 'List' ? 40 : 0}
                position="relative"
                textAlign={variant === 'List' ? 'left' : 'center'}
                borderBottom={
                  variant === 'List' ? 'unset' : `1px solid ${borderColor[(background || 'light') as borderType]}`
                }
                marginBottom={24}
                sm={{ borderBottom: 'unset', marginBottom: 0 }}
              >
                {variant === 'Stat' && feature.stat && (
                  <Heading as="h3" fontSize="displayLg" lineHeight="sm" marginBottom={8} fontWeight="bold">
                    {feature.stat}
                  </Heading>
                )}
                {variant === 'List' ? (
                  <Heading
                    as="h6"
                    fontSize="textXl"
                    lineHeight="lg"
                    marginBottom={4}
                    fontWeight="bold"
                    textColor={backgroundIsDark ? 'foam-50' : 'storm-900'}
                    css={{
                      ul: {
                        listStyle: 'none',
                        padding: 0,
                      },
                    }}
                  >
                    <Icon
                      iconName={feature?.listIcon || 'check'}
                      size={22}
                      color={backgroundIsDark ? colors.lightning[600] : colors.wave[800]}
                      position="absolute"
                      left="0"
                      top="4px"
                    />
                    <ReactMarkdown>{feature.headline || ''}</ReactMarkdown>
                  </Heading>
                ) : (
                  <Text
                    as="span"
                    fontSize="textLg"
                    lineHeight="lg"
                    marginBottom={0}
                    fontWeight="regular"
                    textColor={backgroundIsDark ? 'storm-100' : 'storm-600'}
                    css={{
                      ul: {
                        listStyle: 'none',
                        padding: 0,
                      },
                    }}
                  >
                    <ReactMarkdown>{feature.headline || ''}</ReactMarkdown>
                  </Text>
                )}
                <Paragraph
                  marginBottom={24}
                  textColor={backgroundIsDark ? 'storm-100' : 'storm-600'}
                  fontSize="textXl"
                  lineHeight="lg"
                >
                  {richTextParser(feature.bodyCopy as RichText)}
                </Paragraph>
              </Paragraph>
            ))}
          </Flex>
        )}
      </Flex>
      <Flex
        flexDirection="column"
        justifyContent="center"
        gap={externalVideo ? '40px' : '16px'}
        marginTop={16}
        sm={{
          gap: externalVideo ? '56px' : '16px',
          marginTop: variant === 'centered' ? 24 : 32,
        }}
        md={{
          gap: externalVideo ? '72px' : '16px',
          marginTop: variant === 'centered' ? 24 : 0,
        }}
        lg={{
          flex: '0 0 calc(50% - 30px)',
          width: 'calc(50% - 30px)',
          maxWidth: '100%',
          justifyContent: reverse ? 'start' : 'end',
        }}
        width="100%"
      >
        {externalVideo && (
          <Container position="relative">
            {switchFeaturedMedia && (
              <Image
                maxWidth={variant === 'centered' ? '100%' : '570px'}
                width="100%"
                marginX="auto"
                src={switchFeaturedMedia.url}
                alt={switchFeaturedMedia.title as string}
                responsiveImage={switchFeaturedMedia.responsiveImage as any}
              />
            )}
            <Image
              onClick={handleOpen}
              width="80px"
              height="80px"
              marginX="auto"
              src="https://www.datocms-assets.com/75153/1690304563-play-button.png"
              alt="play"
              position="absolute"
              left="calc(50% - 40px)"
              top="calc(50% - 40px)"
              zIndex={2}
              cursor="pointer"
            />
          </Container>
        )}
        {switchFeaturedMedia && !externalVideo && (
          <Image
            maxWidth={variant === 'centered' ? '100%' : '570px'}
            width="100%"
            marginX="auto"
            src={switchFeaturedMedia.url}
            alt={switchFeaturedMedia.title as string}
            responsiveImage={switchFeaturedMedia.responsiveImage as any}
          />
        )}
      </Flex>
      {externalVideo && (
        <Modal open={open} onClose={handleClose} aria-label={externalVideo?.title} aria-modal="true" role="dialog">
          <Fade in={open}>
            <Flex
              position="absolute"
              top="50%"
              left="50%"
              width="100%"
              maxWidth="700px"
              height="250px"
              transform="translate(-50%, -50%)"
              md={{
                height: '450px',
              }}
            >
              <Iframe
                src={`https://www.youtube.com/embed/${externalVideo.providerUid}`}
                title={externalVideo?.title}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                scrolling="no"
                loading="lazy"
              />
            </Flex>
          </Fade>
        </Modal>
      )}
    </Flex>
  );
};

export default ComponentSwitchback;
